import React, { useState } from 'react';
import MFrame from '@/components/ui/atoms/MFrame';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { IconFileOff, IconPlus } from '@tabler/icons-react';
import { MButton } from '@/components/ui/atoms/MButtons';
import styles from '../dashboard.module.css';
import { MDocumentUploadOptions } from '@/components/ui/molecules/MDocumentUploadOptions';
import { useNavigate } from 'react-router-dom';

const FIXED_COLORS: Record<string, string> = {
  'Prescription': '#274C7E',
  'Reports': '#AACAF3',
  'Procedures': '#6BA2EA',
};
const FIXED_ORDER = ['Prescription', 'Reports', 'Procedures'];

const PIE_COLORS = [
  '#AACAF3',
  '#6BA2EA',
  '#274C7E',
  '#C7DDF7',
  '#E1EDFB',
];

interface DocumentsCardProps {
  year: number;
  data: Array<{ label: string; count: number }>;
  onPrev?: () => void;
  onNext?: () => void;
}

function getPieSegments(data: { label: string; count: number }[]) {
  const total = data.reduce((sum, d) => sum + d.count, 0) || 1;
  let start = 0;
  return data.map((d, i) => {
    const value = d.count / total;
    const color = FIXED_COLORS[d.label] || PIE_COLORS[i % PIE_COLORS.length] || '#CCC';

    const segment = {
      startAngle: start,
      endAngle: start + value * 360,
      color,
    };

    start += value * 360;
    return segment;
  });
}

const DocumentsCard: React.FC<DocumentsCardProps> = ({ year, data, onPrev, onNext }) => {
  const sorted = [...data].sort(
    (a, b) => FIXED_ORDER.indexOf(a.label) - FIXED_ORDER.indexOf(b.label)
  );
  const pieSegments = getPieSegments(sorted);
  const isEmpty = data.every((item) => item.count === 0);
  const [showUploadOptions, setShowUploadOptions] = useState(false);
  const navigate = useNavigate();

  const handleNavigate = () => {
    if (!isEmpty) {
      // Set year and category filter for prescriptions, lab reports, discharge summaries
      const categories = [1, 2, 5];
      const params = new URLSearchParams({
        year: String(year),
        category: categories.join(','),
        backUrl: '/dashboard-detail',
      });
      navigate(`/documents/list?${params.toString()}`);
    }
  };


  return (
    <>
      <div className={styles.ctaCardListTitle}>Health Records</div>
      <div className={styles.dashboardCard} style={{ position: 'relative', height: 'auto' }}>
        <div className={styles.dashboardCenter} style={{marginTop: 10}}>
          {isEmpty ? (
            <>
              <IconFileOff size={64} color="#BCA7E6" className={styles.dashboardIcon} />
              <div className={styles.dashboardEmptyTitle}>
                No health records added for {year}
              </div>

              <MButton
                className={styles.dashboardButton}
                onClick={() => setShowUploadOptions(true)}
                leftIcon={<IconPlus size={28} />}
                size="md"
              >
                Upload health record
              </MButton>

              <MDocumentUploadOptions
                open={showUploadOptions}
                onClose={() => setShowUploadOptions(false)}
                onSelectOption={(option) => {
                  console.log('Selected upload option:', option);
                  setShowUploadOptions(false);
                }}
              />
            </>
          ) : (
            <>
            <p style={{
                color: '#606060',
                fontSize: 12,
                fontWeight: 500,
                textAlign: 'center',
                marginBottom: 24,
              }}>
                Health records include only prescriptions, reports, and procedure uploads.
              </p>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  marginTop: -24,
                  cursor: 'pointer',
                }}
                onClick={handleNavigate}
              >
                <svg width={156} height={156} viewBox="0 0 156 156">
                  <g>
                    {pieSegments.length === 1 ? (
                      <circle cx={78} cy={78} r={70} fill={pieSegments[0].color} />
                    ) : (
                      pieSegments.map((seg, i) => {
                        const r = 70;
                        const cx = 78,
                          cy = 78;
                        const startRad = (seg.startAngle - 90) * (Math.PI / 180);
                        const endRad = (seg.endAngle - 90) * (Math.PI / 180);

                        const x1 = cx + r * Math.cos(startRad);
                        const y1 = cy + r * Math.sin(startRad);
                        const x2 = cx + r * Math.cos(endRad);
                        const y2 = cy + r * Math.sin(endRad);

                        const largeArc = seg.endAngle - seg.startAngle > 180 ? 1 : 0;
                        const path = `M${cx} ${cy} L${x1} ${y1} A${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;

                        return <path key={i} d={path} fill={seg.color} />;
                      })
                    )}

                    <circle cx={78} cy={78} r={52} fill="#fff" />
                  </g>

                  <text
                    x="50%"
                    y="50%"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="24"
                    fontWeight="700"
                    fill="#36116D"
                  >
                    {year}
                  </text>
                </svg>
              </div>

              {/* ---- UPDATED LEGEND BELOW PIE CHART ---- */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    gap: 32,
                  }}
                >
                  {sorted.map((item, i) => (
                    <div
                      key={item.label}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        gap: 6,
                      }}
                    >
                      {/* dot + label */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        <span
                          style={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            background:
                              FIXED_COLORS[item.label] ||
                              PIE_COLORS[i % PIE_COLORS.length],
                          }}
                        />

                        <span
                          style={{
                            color: '#274C7E',
                            fontWeight: 600,
                            fontSize: 12,
                          }}
                        >
                          {item.label}
                        </span>
                      </div>

                      {/* number BELOW label */}
                      <span
                        style={{
                          color: '#36116D',
                          fontWeight: 500,
                          fontSize: 16,
                          marginTop: 2,
                        }}
                      >
                        {item.count}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* ---- END UPDATED LEGEND ---- */}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default DocumentsCard;
